import { useQueryClient, useMutation } from "react-query";
import helpers from "../helpers";

import getAccessToken from "./getAccessToken";

const { REACT_APP_API_URL, REACT_APP_API_KEY } = process.env;

const request = ({ endpoint, token, includeApiKey = false, method = "POST" }) => async (body) => {
  const headers = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (includeApiKey && REACT_APP_API_KEY) {
    headers["x-api-key"] = REACT_APP_API_KEY;
  }
  const url = `${REACT_APP_API_URL}/${endpoint}`.replace(/\/new/, "");
  const res = await fetch(url, {
    method,
    headers,
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => null);
  if (res.status >= 200 && res.status <= 299) {
    return data;
  }
  // `status` y `code` (el motivo que manda el api, ej. agenda_cap_exceeded) para quien
  // necesite distinguir el error; el mensaje queda igual que antes.
  const error = Error(data?.error || "error");
  error.status = res.status;
  if (data?.code) error.code = data.code;
  if (data) error.data = data;
  throw error;
};

const Hook = (endpoint, _options) => {
  const accessToken = getAccessToken();
  const queryClient = useQueryClient();

  const options = JSON.clone(_options || {});
  const includeApiKey = options.includeApiKey || false;
  const method = options.method || "POST";
  delete options.method;

  const findMatchingKeys = (payload) => {
    let key;
    if (endpoint.includes("consumers") && endpoint.includes("notes")) {
      key = endpoint;
    } else if (endpoint.includes("consumers")) {
      key = "consumers";
    } else if (endpoint.includes("events")) {
      key = "events";
    } else if (endpoint.includes("records")) {
      // key = "records";
    }

    if (!key) return [];

    return queryClient
      .getQueryCache()
      .findAll()
      .filter(
        (query) =>
          key.split(",").some((key) => query.queryHash.includes(key)) ||
          (payload?.id && query.queryHash.includes(payload.id))
      )
      // Un query registrado pero nunca fetcheado (enabled:false, o primer
      // fetch todavía en vuelo) tiene data undefined — no hay nada que
      // sincronizar ahí. Sin este filtro, el updater de abajo no matchea
      // ninguna rama, devuelve undefined, y react-query v3 despacha un
      // 'success' FALSO con data undefined que dispara el onSuccess del
      // query dueño (comportamiento documentado de v3: onSuccess también
      // se llama en setQueryData). Bug real en prod: crear/editar un turno
      // en la agenda crasheaba el onSuccess de PaymentLinks
      // ("Cannot read properties of undefined (reading 'filter')") cuando
      // el evento traía paymentLinks embebido y su query pl/events/* quedó
      // deshabilitado sin data.
      .filter((query) => query.state.data !== undefined)
      .map((query) => query.queryKey);
  };

  options.onMutate = (payload) => {
    // Bug real (auditoría 2026-08-14): el update optimista de abajo escribe
    // directo en el cache ANTES de que el server confirme nada — pero si la
    // mutación falla, nada revertía eso. El cache quedaba con datos que el
    // server rechazó, indistinguibles de datos reales, hasta el próximo
    // refetch (que puede tardar minutos o no llegar nunca si nada más
    // invalida esa query). Se guarda acá un snapshot de las keys que
    // `update()` está por tocar para poder restaurarlas en onError.
    let previousEntries = [];
    try {
      if (_options?.onMutate) {
        _options.onMutate(payload);
      }
      previousEntries = findMatchingKeys(payload).map((key) => [
        key,
        queryClient.getQueryData(key),
      ]);
      update(payload);
    } catch (e) {
      console.log(e);
    }
    return { previousEntries };
  };

  options.onSuccess = (data) => {
    try {
      if (_options?.onSuccess) {
        _options.onSuccess(data);
      }
      update(data, "clear");
    } catch (e) {
      console.log(e);
    }
  };

  options.onError = (error, variables, context) => {
    try {
      for (const [key, previousData] of context?.previousEntries || []) {
        queryClient.setQueryData(key, previousData);
      }
      if (_options?.onError) {
        _options.onError(error);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const update = (payload, clear) => {
    if (Array.isArray(payload?.phones)) {
      payload.phones = payload.phones.filter((phone) => phone.value);
    }

    if (Array.isArray(payload?.emails)) {
      payload.emails = payload.emails.filter((email) =>
        helpers.isEmail(email.value)
      );
    }

    const keys = findMatchingKeys(payload);

    let key;
    if (endpoint.includes("consumers") && endpoint.includes("notes")) {
      key = endpoint;
    } else if (endpoint.includes("consumers")) {
      key = "consumers";
    } else if (endpoint.includes("events")) {
      key = "events";
    } else if (endpoint.includes("records")) {
      // key = "records";
    }

    const findIndex = (item) =>
      item.id === (payload.id || payload.createdAt) &&
      (key !== "records" ||
        (item?.consumer?.id === payload?.consumer?.id &&
          item.type === payload.type));

    for (const key of keys) {
      queryClient.setQueryData(key, (_cache) => {
        let cache = JSON.clone(_cache);
        if (cache?.id && cache.id === payload.id) {
          return JSON.clone(payload);
        } else if (Array.isArray(cache?.pages)) {
          for (let i = 0; i < cache.pages.length; i += 1) {
            const index = cache.pages[i].findIndex(findIndex);
            if (index < 0 && (payload.id || payload.createdAt)) {
              cache.pages[i] = [...cache.pages[i], payload].filter(
                (item) => !clear || item.id
              );
            } else {
              cache.pages[i][index] = payload;
            }
          }
        } else if (Array.isArray(cache?.events)) {
          const index = cache.events.findIndex(findIndex);
          if (index < 0) {
            // Bug real (auditoría 2026-08-16): esta rama la matchean TODAS
            // las queries cacheadas con forma /events/query (Agenda del día,
            // Calendar con rango de días, Monitor) porque el match de arriba
            // es por substring genérico "events", no por la query puntual
            // dueña del registro. Si el id mutado no estaba en una de esas
            // listas, antes se agregaba igual — hace falta para que un
            // evento recién creado aparezca sin refetch en la vista activa,
            // pero eso mismo inyectaba un duplicado fantasma en OTRA vista
            // que sigue en cache (cacheTime default 5min) aunque el usuario
            // ya haya cambiado de vista y esté desmontada. Sólo se agrega si
            // la query sigue activa (tiene observers, o sea, está montada):
            // ahí es donde hace falta verlo sin esperar un refetch. Las
            // cacheadas-pero-desmontadas quedan como estaban; su próximo
            // refetch real las va a traer bien.
            const isActive =
              queryClient.getQueryCache().find(key)?.getObserversCount() > 0;
            if (isActive) {
              cache.events = [...cache.events, payload].filter(
                (item) => !clear || item.id
              );
            }
          } else {
            cache.events[index] = payload;
          }
        } else if (Array.isArray(cache)) {
          // Bug real (auditoría 2026-08-16): esta rama identificaba
          // registros únicamente por `.id`/`.createdAt` (el `findIndex` de
          // arriba). Los jobs de historial de Migrate/MigrateToTeam
          // (`/consumers/migrate`, `/consumers/migrateToTeam`) no tienen
          // ninguno de los dos — se identifican por `.key`/`.uid` (igual que
          // Migrate.js, que los busca/filtra por `.key`). Como ningún item
          // tiene `.id`, nunca se encontraban (index < 0) y el payload se
          // appendeaba siempre sin verificar identidad. Peor: en el success
          // path (`clear`) el filtro `!clear || item.id` descartaba TODOS
          // los items sin `.id` — o sea, todo el historial — cada vez que
          // CUALQUIER otra mutación del bucket genérico "consumers"
          // resolvía mientras esta query seguía cacheada (cacheTime default,
          // sin refetch todavía), vaciando "Archivos procesados" en
          // silencio. Ahora la identidad también reconoce `.key`/`.uid`
          // tanto para encontrar el registro a reemplazar como para decidir
          // si sobrevive al filtro de `clear`, sin cambiar nada para los
          // registros que sí usan `.id`/`.createdAt`.
          const hasIdentity = (item) =>
            !!(item?.id || item?.createdAt || item?.key || item?.uid);
          const matchesPayload = (item) =>
            payload.id || payload.createdAt
              ? item.id === (payload.id || payload.createdAt)
              : (payload.key && item.key === payload.key) ||
                (payload.uid && item.uid === payload.uid);
          const index = cache.findIndex(matchesPayload);
          if (index < 0) {
            cache = hasIdentity(payload)
              ? [...cache, payload].filter((item) => !clear || hasIdentity(item))
              : cache;
          } else {
            cache[index] = payload;
          }
        }
        return JSON.clone(cache);
      });
    }
  };

  const { mutate, ...rest } = useMutation(
    request({
      endpoint,
      token: accessToken.value,
      includeApiKey,
      method,
    }),
    options
  );

  return [mutate, rest];
};

export default Hook;
